<div class="reginb">
  <header class="reginb__header">
    <div>
      <h1 class="reginb__title">
        <span class="reginb__title-icon">
          <i class="fa-solid fa-file-invoice-dollar" aria-hidden="true"></i>
        </span>
        {{ 'REGISTRATIONS.TITLE' | translate }}
      </h1>
      <p class="reginb__subtitle">{{ 'REGISTRATIONS.SUBTITLE' | translate }}</p>
    </div>
  </header>

  @if (!canWrite) { <app-read-only-notice messageKey="REGISTRATIONS.READ_ONLY_HINT"></app-read-only-notice> }

  <app-filter-bar
    [activeCount]="activeFilterCount"
    [search]="search"
    searchPlaceholderKey="REGISTRATIONS.SEARCH_PLACEHOLDER"
    (searchChange)="onSearchChange($event)"
    (reset)="resetFilters()">

    <div class="reginb__seg">
      @for (status of statuses; track status) {
        <button type="button" [class.is-active]="statusFilter === status"
                (click)="onStatusFilter(status)">
          {{ statusLabelKey(status) | translate }}
          <!-- Le compte vient du serveur : le filtrage y est fait, la liste
               locale ne connait que le statut courant. -->
          @if (countFor(status) !== null) {
            <span class="reginb__seg-count">{{ countFor(status) }}</span>
          }
        </button>
      }
    </div>
  </app-filter-bar>

  <app-data-table
    [columns]="columns"
    [rows]="rows"
    [rowTemplate]="rowTpl"
    [loading]="isLoading"
    [errored]="hasError"
    [page]="pagination"
    [pageSize]="pageSize"
    [pageSizes]="pageSizes"
    [showPager]="showPager"
    (pageChange)="onPage($event)"
    (pageSizeChange)="onPageSize($event)"
    emptyIcon="file-invoice-dollar"
    emptyTitleKey="REGISTRATIONS.EMPTY_TITLE"
    emptyMessageKey="REGISTRATIONS.EMPTY_MESSAGE"
    errorTitleKey="REGISTRATIONS.ERROR_TITLE"
    errorMessageKey="REGISTRATIONS.ERROR_MESSAGE"
    (retry)="load()">
  </app-data-table>
</div>

<ng-template #rowTpl let-r>
  <td><code class="reginb__ref">{{ r.reference }}</code></td>
  <td>{{ r.firstName }} {{ r.lastName }}</td>
  <td>{{ r.email }}</td>
  <td>{{ r.packNameFr }}</td>
  <td class="text-end">{{ r.amountComputed }} {{ r.currency }}</td>
  <td class="text-center">
    <span class="reginb__pill" [attr.data-status]="r.status">
      {{ statusLabelKey(r.status) | translate }}
    </span>
  </td>
  <td class="text-center">
    <button type="button" class="reginb__action" (click)="openDetail(r)"
            [title]="'REGISTRATIONS.REVIEW' | translate"
            [attr.aria-label]="'REGISTRATIONS.REVIEW' | translate">
      <i class="fa-solid fa-eye" aria-hidden="true"></i>
    </button>
  </td>
</ng-template>

<!-- ── Détail : contrôle du justificatif et décision ── -->
<app-modal [show]="isDetailOpen" size="xl"
           [title]="'REGISTRATIONS.DETAIL_TITLE' | translate"
           (closed)="closeDetail()">

  @if (isDetailLoading) {
    <p class="reginb__loading">{{ 'COMMON.LOADING' | translate }}</p>
  }

  @if (!isDetailLoading && detail; as d) {
    <div class="reginb__detail">

      <div class="reginb__detail-head">
        <span class="reginb__pill" [attr.data-status]="d.status">
          {{ statusLabelKey(d.status) | translate }}
        </span>
        <code class="reginb__ref">{{ d.reference }}</code>
      </div>

      <div class="reginb__cols">
        <!-- Ce que l'élève a demandé -->
        <dl class="reginb__facts">
          <div><dt>{{ 'REGISTRATIONS.NAME' | translate }}</dt><dd>{{ d.firstName }} {{ d.lastName }}</dd></div>
          <div><dt>{{ 'REGISTRATIONS.EMAIL' | translate }}</dt><dd>{{ d.email }}</dd></div>
          <div><dt>{{ 'REGISTRATIONS.PHONE' | translate }}</dt><dd>{{ d.phone || '—' }}</dd></div>
          <div><dt>{{ 'REGISTRATIONS.PARENT_NAME' | translate }}</dt><dd>{{ parentName(d) }}</dd></div>
          <div><dt>{{ 'REGISTRATIONS.PARENT_PHONE' | translate }}</dt><dd>{{ d.parentPhone || '—' }}</dd></div>
          <div><dt>{{ 'REGISTRATIONS.PARENT_EMAIL' | translate }}</dt><dd>{{ d.parentEmail || '—' }}</dd></div>
          <div><dt>{{ 'REGISTRATIONS.REGION' | translate }}</dt><dd>{{ d.regionName || '—' }}</dd></div>
          <div><dt>{{ 'REGISTRATIONS.PACK' | translate }}</dt><dd>{{ d.packNameFr }}</dd></div>
          <div><dt>{{ 'REGISTRATIONS.PATH' | translate }}</dt><dd>{{ d.learningPathTitle }}</dd></div>
          <div><dt>{{ 'REGISTRATIONS.SUBJECTS' | translate }}</dt><dd>{{ d.subjects.join(' · ') }}</dd></div>
          <div class="reginb__fact-amount">
            <dt>{{ 'REGISTRATIONS.AMOUNT' | translate }}</dt>
            <dd>{{ d.amountComputed }} {{ d.currency }}</dd>
          </div>
        </dl>

        <!-- Le justificatif : c'est la pièce à contrôler -->
        <div class="reginb__proof">
          @if (d.proofUrl) {
            <p class="reginb__proof-meta">
              <i class="fa-solid fa-paperclip" aria-hidden="true"></i>
              {{ d.proofOriginalFileName }}
              <span>{{ d.proofSubmittedAt | localizedDate: 'short' }}</span>
            </p>

            @if (proofIsImage) {
              <a [href]="d.proofUrl" target="_blank" rel="noopener">
                <img class="reginb__proof-img" [src]="d.proofUrl"
                     [alt]="'REGISTRATIONS.PROOF' | translate" />
              </a>
            } @else {
              <a class="reginb__proof-link" [href]="d.proofUrl" target="_blank" rel="noopener">
                <i class="fa-solid fa-file-pdf" aria-hidden="true"></i>
                {{ 'REGISTRATIONS.OPEN_PROOF' | translate }}
              </a>
            }
            <p class="reginb__proof-hint">{{ 'REGISTRATIONS.PROOF_EXPIRES' | translate }}</p>
          } @else {
            <p class="reginb__proof-none">
              <i class="fa-solid fa-hourglass-half" aria-hidden="true"></i>
              {{ 'REGISTRATIONS.NO_PROOF' | translate }}
            </p>
          }

          @if (d.rejectionReason) {
            <p class="reginb__reason">
              <strong>{{ 'REGISTRATIONS.LAST_REASON' | translate }}</strong>
              {{ d.rejectionReason }}
            </p>
          }
        </div>
      </div>

      @if (pendingDecision) {
        <div class="reginb__reject">
          <label for="decision-reason">
            {{ (pendingDecision === 'reject' ? 'REGISTRATIONS.REASON_LABEL'
                                             : 'REGISTRATIONS.WAIVE_REASON_LABEL') | translate }}
          </label>
          <textarea id="decision-reason" rows="3" [(ngModel)]="reason"
                    [placeholder]="(pendingDecision === 'reject' ? 'REGISTRATIONS.REASON_PLACEHOLDER'
                                                                : 'REGISTRATIONS.WAIVE_REASON_PLACEHOLDER') | translate"></textarea>
          <p class="reginb__reject-hint">
            {{ (pendingDecision === 'reject' ? 'REGISTRATIONS.REASON_HINT'
                                             : 'REGISTRATIONS.WAIVE_REASON_HINT') | translate }}
          </p>
        </div>
      }

      <div class="reginb__actions">
        @if (d.status !== 'validated' && !canWrite) {
          <!-- Consultation seule : la demande se lit, aucune décision ne se prend ici. -->
          <app-read-only-notice messageKey="REGISTRATIONS.READ_ONLY_HINT"></app-read-only-notice>
        } @else if (d.status !== 'validated') {
          @if (pendingDecision) {
            <button type="button" class="reginb__btn" (click)="cancelDecision()" [disabled]="isActing">
              {{ 'COMMON.CANCEL' | translate }}
            </button>
            <button type="button" class="reginb__btn"
                    [class.reginb__btn--danger]="pendingDecision === 'reject'"
                    [class.reginb__btn--warn]="pendingDecision === 'waive'"
                    (click)="confirmDecision()"
                    [disabled]="isActing || reasonTooShort">
              {{ (pendingDecision === 'reject' ? 'REGISTRATIONS.CONFIRM_REJECT'
                                               : 'REGISTRATIONS.CONFIRM_WAIVE') | translate }}
            </button>
          } @else {
            <button type="button" class="reginb__btn" (click)="resendActivation()" [disabled]="isActing">
              <i class="fa-solid fa-envelope" aria-hidden="true"></i>
              {{ 'REGISTRATIONS.RESEND_ACTIVATION' | translate }}
            </button>
            <!-- Ouvrir l'accès sans paiement : possible, mais motivé et tracé. -->
            <button type="button" class="reginb__btn reginb__btn--warn"
                    (click)="startDecision('waive')" [disabled]="isActing">
              <i class="fa-solid fa-hand-holding-heart" aria-hidden="true"></i>
              {{ 'REGISTRATIONS.WAIVE' | translate }}
            </button>
            <button type="button" class="reginb__btn reginb__btn--danger"
                    (click)="startDecision('reject')" [disabled]="isActing">
              <i class="fa-solid fa-xmark" aria-hidden="true"></i>
              {{ 'REGISTRATIONS.REJECT' | translate }}
            </button>
            <button type="button" class="reginb__btn reginb__btn--primary"
                    (click)="approve()" [disabled]="isActing || !d.hasProof"
                    [title]="!d.hasProof ? ('REGISTRATIONS.APPROVE_NEEDS_PROOF' | translate) : ''">
              <i class="fa-solid fa-check" aria-hidden="true"></i>
              {{ 'REGISTRATIONS.APPROVE' | translate }}
            </button>
          }
        } @else {
          <p class="reginb__done">
            <i class="fa-solid fa-circle-check" aria-hidden="true"></i>
            {{ (d.isFeeWaived ? 'REGISTRATIONS.VALIDATED_WITHOUT_PAYMENT'
                              : 'REGISTRATIONS.ALREADY_VALIDATED') | translate }}
          </p>
        }
      </div>

      <!-- Journal : qui a fait quoi, quand, pourquoi. Sans lui, une validation
           sans paiement serait indiscernable d'une validation ordinaire. -->
      <section class="reginb__audit">
        <h3 class="reginb__audit-title">
          <i class="fa-solid fa-clock-rotate-left" aria-hidden="true"></i>
          {{ 'REGISTRATIONS.AUDIT_TITLE' | translate }}
        </h3>

        @if (d.auditTrail.length === 0) {
          <p class="reginb__audit-empty">{{ 'REGISTRATIONS.AUDIT_EMPTY' | translate }}</p>
        } @else {
          <ol class="reginb__audit-list">
            @for (entry of d.auditTrail; track entry.id) {
              <li class="reginb__audit-item" [attr.data-action]="entry.action">
                <span class="reginb__audit-icon">
                  <i class="fa-solid" [ngClass]="auditIcon(entry.action)" aria-hidden="true"></i>
                </span>
                <div class="reginb__audit-body">
                  <p class="reginb__audit-head">
                    <strong>{{ auditLabelKey(entry.action) | translate }}</strong>
                    <span class="reginb__audit-actor">{{ entry.actorName || '—' }}</span>
                    <time>{{ entry.createdAt | localizedDate: 'short' }}</time>
                  </p>
                  @if (entry.comment) {
                    <p class="reginb__audit-comment">{{ entry.comment }}</p>
                  }
                </div>
              </li>
            }
          </ol>
        }
      </section>
    </div>
  }
</app-modal>
